import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  FaCalendarPlus,
  FaSignInAlt,
  FaChevronRight,
} from "react-icons/fa";
import { getMyHostedEvents, getMyJoinedEvents } from "../utils/events";
import { useNotifications } from "./NotificationsContext";

// The single "Events" hub on the home dashboard. It combines the two entry
// points (start a new event / join one by code) with the list of events you're
// currently in - both the ones you host and the ones you've joined. That list
// used to live on the Start and Join screens; consolidating it here keeps
// everything about events in one place and leaves those screens as simple forms.
const EventsCard = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const { unreadByCode } = useNotifications();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }
      // Load hosted and joined independently - if one query fails (e.g. a
      // transient error), the other should still show rather than both vanish.
      const [hostedRes, joinedRes] = await Promise.allSettled([
        getMyHostedEvents(user.uid),
        getMyJoinedEvents(user.uid),
      ]);
      if (hostedRes.status === "rejected") {
        console.error("Error loading hosted events:", hostedRes.reason);
      }
      if (joinedRes.status === "rejected") {
        console.error("Error loading joined events:", joinedRes.reason);
      }
      const hosted = hostedRes.status === "fulfilled" ? hostedRes.value : [];
      const joined = joinedRes.status === "fulfilled" ? joinedRes.value : [];

      // Merge and tag each with the viewer's role. Dedupe by code, letting
      // "host" win if you somehow both host and joined the same one.
      const byCode = new Map();
      joined.forEach((e) => byCode.set(e.code, { ...e, role: "guest" }));
      hosted.forEach((e) => byCode.set(e.code, { ...e, role: "host" }));
      setEvents(
        [...byCode.values()].sort(
          (a, b) =>
            (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0),
        ),
      );
      setLoading(false);
    });
    return () => unsub();
  }, [auth]);

  // Title for a row: a custom event name wins; otherwise the host's own event
  // shows its address title, and a joined event shows whose event it is.
  const titleFor = (e) =>
    e.name || (e.role === "host" ? e.addressTitle : `${e.hostName}'s event`);

  const actionButtonStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    padding: "14px 10px",
    borderRadius: "12px",
    border: "1px solid var(--border-color, #ddd)",
    backgroundColor: "var(--item-bg, #fff)",
    color: "var(--text-primary, #1a1a1a)",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
  };

  return (
    <div
      style={{
        // Flex column so the events list can scroll inside the card while the
        // header + Start/Join buttons stay put. minHeight:0 lets the card
        // shrink to its bounded parent (see Home) instead of growing the page.
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        borderRadius: "16px",
        border: "1px solid var(--border-color, #ddd)",
        backgroundColor: "var(--item-bg, #fff)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        padding: "18px 16px",
      }}
    >
      <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: "4px" }}>
        Events
      </div>
      <div
        style={{
          fontSize: "12.5px",
          color: "var(--text-secondary, #777)",
          marginBottom: "14px",
          lineHeight: 1.3,
        }}
      >
        Share an address by code, or join one.
      </div>

      {/* Two entry points, side by side. */}
      <div style={{ display: "flex", gap: "12px" }}>
        <button
          type="button"
          onClick={() => navigate("/start-event")}
          style={actionButtonStyle}
        >
          <FaCalendarPlus size={20} color="#f5a623" />
          Start new event
        </button>
        <button
          type="button"
          onClick={() => navigate("/join-event")}
          style={actionButtonStyle}
        >
          <FaSignInAlt size={20} color="#9b59b6" />
          Join event
        </button>
      </div>

      {/* Events you're currently in - hosted and joined together. The list
          scrolls inside the card once it outgrows the space; the label and the
          buttons above stay visible. */}
      {!loading && events.length > 0 && (
        <div
          style={{
            marginTop: "18px",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flexShrink: 0,
              fontSize: "16px",
              fontWeight: 700,
              marginBottom: "8px",
            }}
          >
            Your active events
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {events.map((e) => (
            <button
              key={e.code}
              type="button"
              onClick={() => navigate(`/event/${e.code}`)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                width: "100%",
                textAlign: "left",
                padding: "12px 14px",
                marginBottom: "8px",
                borderRadius: "10px",
                border: "1px solid var(--border-color, #eee)",
                backgroundColor: "var(--chip-bg, #f6f6f6)",
                color: "var(--text-primary, #1a1a1a)",
                cursor: "pointer",
              }}
            >
              <span style={{ minWidth: 0 }}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {titleFor(e)}
                  </span>
                  {/* Small badge so it's obvious which are yours vs joined. */}
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      padding: "2px 7px",
                      borderRadius: "999px",
                      color: e.role === "host" ? "#2e7d32" : "#6a1b9a",
                      backgroundColor:
                        e.role === "host"
                          ? "rgba(76,175,80,0.15)"
                          : "rgba(155,89,182,0.15)",
                    }}
                  >
                    {e.role === "host" ? "Hosting" : "Joined"}
                  </span>
                  {/* The host moved the address and this guest hasn't opened it
                      yet - flag it so it's obvious which event changed. */}
                  {unreadByCode[e.code] > 0 && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        padding: "2px 7px",
                        borderRadius: "999px",
                        color: "#fff",
                        backgroundColor: "#e53935",
                      }}
                    >
                      Address updated
                    </span>
                  )}
                </span>
                <span
                  style={{
                    display: "block",
                    color: "var(--text-secondary, #777)",
                    fontSize: "13px",
                    letterSpacing: "1px",
                    marginTop: "2px",
                  }}
                >
                  {e.code}
                </span>
              </span>
              <FaChevronRight size={13} color="var(--text-secondary, #999)" />
            </button>
          ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsCard;
